import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { ImpersonationContextViewModel } from '../../../../viewmodels/AdminViewModels';
import { Collapsible } from '../../../components/Collapsible';
import { AdvancedOptionsButton } from '../../../components/automation/AutomationStepCard/presentation';
import { StepSendFromOption } from '../../../components/automation/AutomationStepCard/components/StepSendFromOption';
import { styleSheet } from '../../../components/automation/AutomationStepCard/styles';
import { ConditionalFilter } from './ConditionalFilter';
import type { ITextingAutomationStepExtended } from '../types/automationSteps';

interface ConnectionTypesProps {
	connectionTypes?: Api.IConnectionType[];
}

interface TextingStepAdvancedOptionsProps extends ConnectionTypesProps {
	step: ITextingAutomationStepExtended;
	stepId: string;
	nodeId: string;
	filter: Api.IContactsFilterRequest | null | undefined;
	canEdit: boolean;
	isExpanded: boolean;
	onToggle: () => void;
	onSaveSenderOptions: (option: Api.ISendFromOptions, sendToSender: boolean) => void;
	onFilterChange: (filter: Api.IContactsFilterRequest | null) => void;
	extraOptions?: React.ReactNode;
	impersonationContext?: Api.IImpersonationContext;
}

/**
 * Advanced options section for texting automation steps.
 * Includes sender options and conditional contact filtering.
 */
export function TextingStepAdvancedOptions({
	step,
	stepId,
	nodeId,
	filter,
	canEdit,
	isExpanded,
	onToggle,
	onSaveSenderOptions,
	onFilterChange,
	extraOptions,
	impersonationContext,
	connectionTypes,
}: TextingStepAdvancedOptionsProps) {
	const impersonationContextViewModel = React.useMemo(
		() => (impersonationContext ? new ImpersonationContextViewModel(impersonationContext) : undefined),
		[impersonationContext]
	);

	return (
		<>
			<AdvancedOptionsButton isCollapsed={!isExpanded} onClick={onToggle} />
			<Collapsible isCollapsed={!isExpanded}>
				<div className={css(styleSheet.advancedOptionsContent)}>
					<StepSendFromOption
						showInternalSendFromOption
						step={step}
						isBusy={step?.isBusy ?? false}
						onSave={onSaveSenderOptions}
						connectionTypes={connectionTypes}
						impersonationContext={impersonationContextViewModel}
						canEdit={canEdit}
					/>
					{extraOptions}
					<ConditionalFilter
						stepId={stepId || nodeId}
						filter={filter}
						canEdit={canEdit}
						isBusy={step?.isBusy}
						onFilterChange={onFilterChange}
					/>
				</div>
			</Collapsible>
		</>
	);
}
